/**
 * Collection / Search AJAX filter, sort & pagination
 *
 * - Intercepts filter form submits, sort changes, and pagination link clicks
 * - Fetches new page HTML via Shopify's ?sections= rendering API
 * - Swaps the results container innerHTML and updates the URL
 * - Shows a skeleton loader overlay while fetching
 *
 * Works for both collection pages (#collection-results, section: main-collection)
 * and search pages (#search-results, section: main-search).
 * The results container must carry a data-section-id attribute for section detection.
 */
(function () {
  /** Resolve the active results container and its Shopify section ID. */
  function getResultsConfig() {
    const byId = (id) => document.getElementById(id);
    const container = byId('collection-results') || byId('search-results');
    if (!container) return null;
    const sectionId = container.dataset.sectionId || 'main-collection';
    return { container, sectionId };
  }

  /** Show/hide the full-screen loading overlay */
  function setLoading(loading) {
    const overlay = document.getElementById('collection-loader');
    if (!overlay) return;
    if (loading) {
      overlay.style.display = 'grid';
      // One rAF so the display change paints before we trigger the opacity transition
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
        overlay.style.pointerEvents = 'auto';
      });
    } else {
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
      overlay.addEventListener(
        'transitionend',
        () => {
          overlay.style.display = 'none';
        },
        { once: true },
      );
    }
  }

  /**
   * Fetch a new page and swap only the results region.
   * @param {string} url - The URL to fetch (with updated query params)
   */
  async function applyFilters(url) {
    const config = getResultsConfig();
    if (!config) return;

    const { container, sectionId } = config;

    setLoading(true);

    try {
      const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}sections=${sectionId}`;
      const response = await fetch(fetchUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const html = data[sectionId];
      if (!html) throw new Error('Section not found in response');

      // Parse the new section HTML and extract just the results region
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newResults = doc.getElementById(container.id);

      if (newResults) {
        container.innerHTML = newResults.innerHTML;
      }

      // Update URL without reload
      history.pushState({}, '', url);

      // Scroll to top of results region
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Re-bind pagination links in the freshly swapped content
      bindPaginationLinks(container);
    } catch (err) {
      console.error('[collection-filter] fetch error:', err);
      // Fall back to a normal navigation on error
      window.location = url;
    } finally {
      setLoading(false);
    }
  }

  /** Collect all active filter + sort values from a form into a URL */
  function buildUrlFromForm(form) {
    const data = new FormData(form);
    const params = new URLSearchParams();

    // Preserve current page path
    const base = window.location.pathname;

    for (const [key, value] of data.entries()) {
      if (value !== '') params.append(key, value);
    }

    // Retain any existing sort_by if not included in this form
    if (!params.has('sort_by')) {
      const existing = new URLSearchParams(window.location.search).get('sort_by');
      if (existing) params.set('sort_by', existing);
    }

    return `${base}?${params.toString()}`;
  }

  /** Intercept filter form submit */
  function bindFilterForms() {
    document.querySelectorAll('.js-filter-form').forEach((form) => {
      // Checkbox / select auto-submit
      form.addEventListener('change', (e) => {
        e.preventDefault();
        applyFilters(buildUrlFromForm(form));
      });

      // Explicit submit (price range "Go" button)
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        applyFilters(buildUrlFromForm(form));
      });
    });
  }

  /** Intercept sort form */
  function bindSortForm() {
    document.querySelectorAll('.js-sort-form').forEach((form) => {
      form.addEventListener('change', (e) => {
        e.preventDefault();
        applyFilters(buildUrlFromForm(form));
      });
    });
  }

  /** Fire filter when focus leaves a price range input (no Go button needed) */
  function bindPriceInputs() {
    document.querySelectorAll('.js-price-input').forEach((input) => {
      input.addEventListener('blur', () => {
        const form = input.closest('.js-filter-form');
        if (form) applyFilters(buildUrlFromForm(form));
      });
    });
  }

  /** Intercept pagination links inside results container */
  function bindPaginationLinks(container) {
    container.querySelectorAll('.js-pagination a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        applyFilters(link.href);
      });
    });
  }

  /** Handle browser back/forward navigation */
  window.addEventListener('popstate', () => {
    applyFilters(window.location.href);
  });

  /** Boot */
  function init() {
    bindFilterForms();
    bindSortForm();
    bindPriceInputs();
    const config = getResultsConfig();
    if (config) bindPaginationLinks(config.container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
